// src/utils/migrationUtility.tsx
// Database Migration Utility for Adding Sharing Fields
// Run this once to migrate existing users and videos to new schema

import React from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  writeBatch,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Migration status tracking
 */
interface MigrationResult {
  success: boolean;
  usersUpdated: number;
  videosUpdated: number;
  sharesUpdated: number;  // NEW
  errors: string[];
}

/**
 * Migrate existing shares to Phase 2 format
 */
export const migrateSharesForPhase2 = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    usersUpdated: 0,
    videosUpdated: 0,
    sharesUpdated: 0,
    errors: []
  };

  try {
    console.log('🟡 Starting share migration for Phase 2...');
    
    const sharesRef = collection(db, 'sharedSessions');
    const snapshot = await getDocs(sharesRef);
    
    if (snapshot.empty) {
      console.log('⏭️ No shares to migrate');
      return result;
    }
    
    console.log(`📊 Found ${snapshot.size} shares to migrate`);
    
    const batch = writeBatch(db);
    let batchCount = 0;
    
    for (const shareDoc of snapshot.docs) {
      const data = shareDoc.data();
      
      // Check if already migrated
      if (data.ownerId && data.title && data.maxViews !== undefined && data.isActive !== undefined) {
        console.log(`⏭️ Skipping share ${shareDoc.id} - already migrated`);
        continue;
      }
      
      // Migrate old fields to new format
      const updates: any = {};
      
      // Map old field names to new
      if (data.createdBy && !data.ownerId) {
        updates.ownerId = data.createdBy;
      }
      
      if (data.videoTitle && !data.title) {
        updates.title = data.videoTitle;
      }
      
      // Add new required Phase 2 fields
      if (data.maxViews === undefined) {
        updates.maxViews = 30;
      }
      
      if (data.isActive === undefined) {
        updates.isActive = true;
      }
      
      // Only update if there are changes
      if (Object.keys(updates).length > 0) {
        updates.updatedAt = Timestamp.now();
        batch.update(shareDoc.ref, updates);
        batchCount++;
        result.sharesUpdated++;
      }
      
      // Firestore batch limit is 500 operations
      if (batchCount >= 500) {
        await batch.commit();
        console.log(`✅ Committed batch of ${batchCount} share updates`);
        batchCount = 0;
      }
    }
    
    // Commit remaining updates
    if (batchCount > 0) {
      await batch.commit();
      console.log(`✅ Committed final batch of ${batchCount} share updates`);
    }
    
    console.log(`✅ Share migration complete: ${result.sharesUpdated} shares updated`);
    
  } catch (error) {
    console.error('❌ Error during share migration:', error);
    result.success = false;
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
  }
  
  return result;
};

/**
 * Migrate all existing users to include sharing fields
 */
export const migrateUsersForSharing = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    usersUpdated: 0,
    videosUpdated: 0,
    sharesUpdated: 0,
    errors: []
  };

  try {
    console.log('🟡 Starting user migration for sharing feature...');
    
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    console.log(`📊 Found ${snapshot.size} users to migrate`);
    
    const batch = writeBatch(db);
    let batchCount = 0;
    
    for (const userDoc of snapshot.docs) {
      const data = userDoc.data();
      
      // Check if user already has sharing fields
      if (data.activeSharesCount !== undefined) {
        console.log(`⏭️ Skipping user ${userDoc.id} - already migrated`);
        continue;
      }
      
      // Add sharing fields to existing user
      const userRef = doc(db, 'users', userDoc.id);
      batch.update(userRef, {
        activeSharesCount: 0,
        totalSharesCreated: 0,
        totalShareViews: 0,
        // Preserve existing data
        updatedAt: Timestamp.now()
      });
      
      batchCount++;
      result.usersUpdated++;
      
      // Firestore batch limit is 500 operations
      if (batchCount >= 500) {
        await batch.commit();
        console.log(`✅ Committed batch of ${batchCount} user updates`);
        batchCount = 0;
      }
    }
    
    // Commit remaining updates
    if (batchCount > 0) {
      await batch.commit();
      console.log(`✅ Committed final batch of ${batchCount} user updates`);
    }
    
    console.log(`✅ User migration complete: ${result.usersUpdated} users updated`);
    
  } catch (error) {
    console.error('❌ Error during user migration:', error);
    result.success = false;
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
  }
  
  return result;
};

/**
 * Migrate all existing video metadata to include sharing fields
 */
export const migrateVideosForSharing = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    usersUpdated: 0,
    videosUpdated: 0,
    sharesUpdated: 0,
    errors: []
  };

  try {
    console.log('🟡 Starting video metadata migration for sharing feature...');
    
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    for (const userDoc of usersSnapshot.docs) {
      const videosRef = collection(db, 'users', userDoc.id, 'videos');
      const videosSnapshot = await getDocs(videosRef);
      
      if (videosSnapshot.empty) {
        console.log(`⏭️ User ${userDoc.id} has no videos`);
        continue;
      }
      
      console.log(`📊 Found ${videosSnapshot.size} videos for user ${userDoc.id}`);
      
      const batch = writeBatch(db);
      let batchCount = 0;
      
      for (const videoDoc of videosSnapshot.docs) {
        const data = videoDoc.data();
        
        // Check if video already has sharing fields
        if (data.currentShareIsActive !== undefined) {
          console.log(`⏭️ Skipping video ${videoDoc.id} - already migrated`);
          continue;
        }
        
        // Add sharing fields to existing video
        const videoRef = doc(db, 'users', userDoc.id, 'videos', videoDoc.id);
        batch.update(videoRef, {
          currentShareId: null,
          currentShareUrl: null,
          currentShareExpiresAt: null,
          currentShareViewCount: 0,
          currentShareMaxViews: 30,
          currentShareIsActive: false,
          currentShareCreatedAt: null,
          totalSharesCreated: 0,
          totalShareViews: 0,
          // Preserve existing data
          updatedAt: Timestamp.now()
        });
        
        batchCount++;
        result.videosUpdated++;
        
        // Firestore batch limit is 500 operations
        if (batchCount >= 500) {
          await batch.commit();
          console.log(`✅ Committed batch of ${batchCount} video updates`);
          batchCount = 0;
        }
      }
      
      // Commit remaining updates for this user
      if (batchCount > 0) {
        await batch.commit();
        console.log(`✅ Committed final batch of ${batchCount} video updates for user ${userDoc.id}`);
      }
    }
    
    console.log(`✅ Video migration complete: ${result.videosUpdated} videos updated`);
    
  } catch (error) {
    console.error('❌ Error during video migration:', error);
    result.success = false;
    result.errors.push(error instanceof Error ? error.message : 'Unknown error');
  }
  
  return result;
};

/**
 * Run complete migration for users, videos, AND shares
 */
export const runCompleteMigration = async (): Promise<{
  users: MigrationResult;
  videos: MigrationResult;
  shares: MigrationResult;
}> => {
  console.log('🚀 Starting complete database migration for sharing feature...');
  console.log('⚠️ This may take several minutes for large databases');
  
  const userResult = await migrateUsersForSharing();
  const videoResult = await migrateVideosForSharing();
  const shareResult = await migrateSharesForPhase2();
  
  console.log('\n📊 MIGRATION SUMMARY:');
  console.log('Users:');
  console.log(`  ✅ Updated: ${userResult.usersUpdated}`);
  console.log(`  ❌ Errors: ${userResult.errors.length}`);
  
  console.log('Videos:');
  console.log(`  ✅ Updated: ${videoResult.videosUpdated}`);
  console.log(`  ❌ Errors: ${videoResult.errors.length}`);
  
  console.log('Shares:');
  console.log(`  ✅ Updated: ${shareResult.sharesUpdated}`);
  console.log(`  ❌ Errors: ${shareResult.errors.length}`);
  
  const overallSuccess = userResult.success && videoResult.success && shareResult.success;
  console.log(`\n${overallSuccess ? '✅' : '❌'} Migration ${overallSuccess ? 'completed successfully' : 'completed with errors'}`);
  
  return {
    users: userResult,
    videos: videoResult,
    shares: shareResult
  };
};

/**
 * Verify migration completed successfully
 */
export const verifyMigration = async (): Promise<{
  usersOk: boolean;
  videosOk: boolean;
  details: string[];
}> => {
  const details: string[] = [];
  let usersOk = true;
  let videosOk = true;
  
  try {
    // Check random sample of users
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    let checkedUsers = 0;
    let usersWithFields = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const data = userDoc.data();
      if (data.activeSharesCount !== undefined) {
        usersWithFields++;
      }
      checkedUsers++;
      if (checkedUsers >= 10) break; // Sample first 10 users
    }
    
    details.push(`Users: ${usersWithFields}/${checkedUsers} have sharing fields`);
    if (usersWithFields < checkedUsers) {
      usersOk = false;
      details.push('⚠️ Some users missing sharing fields');
    }
    
    // Check random sample of videos
    let checkedVideos = 0;
    let videosWithFields = 0;
    
    for (const userDoc of usersSnapshot.docs) {
      const videosRef = collection(db, 'users', userDoc.id, 'videos');
      const videosSnapshot = await getDocs(videosRef);
      
      for (const videoDoc of videosSnapshot.docs) {
        const data = videoDoc.data();
        if (data.currentShareIsActive !== undefined) {
          videosWithFields++;
        }
        checkedVideos++;
        if (checkedVideos >= 10) break; // Sample first 10 videos
      }
      if (checkedVideos >= 10) break;
    }
    
    details.push(`Videos: ${videosWithFields}/${checkedVideos} have sharing fields`);
    if (videosWithFields < checkedVideos && checkedVideos > 0) {
      videosOk = false;
      details.push('⚠️ Some videos missing sharing fields');
    }
    
  } catch (error) {
    details.push(`❌ Verification error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    usersOk = false;
    videosOk = false;
  }
  
  return { usersOk, videosOk, details };
};

/**
 * Helper: Add migration button to admin panel
 * Usage: Add this to your app's admin/settings page
 */
export const MigrationButton = () => {
  const handleMigration = async () => {
    if (!window.confirm('This will migrate all users and videos. Continue?')) {
      return;
    }
    
    try {
      const result = await runCompleteMigration();
      
      if (result.users.success && result.videos.success) {
        alert(`✅ Migration successful!\nUsers: ${result.users.usersUpdated}\nVideos: ${result.videos.videosUpdated}`);
      } else {
        const errors = [...result.users.errors, ...result.videos.errors];
        alert(`⚠️ Migration completed with errors:\n${errors.join('\n')}`);
      }
    } catch (error) {
      alert(`❌ Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  
  return (
    <button 
      onClick={handleMigration}
      style={{
        padding: '10px 20px',
        backgroundColor: '#FF6B00',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold'
      }}
    >
      🚀 Run Database Migration
    </button>
  );
};